import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Calendar,
  Clock,
  Video,
  User,
  Mail,
  FileText,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Loader2,
  ExternalLink,
  Globe,
} from 'lucide-react'
import { useBookingPage, useCreateBooking } from '@/lib/data/hooks/useBooking'
import { useAvailabilityForDateRange } from '@/lib/data/hooks/useAvailability'
import type { TimeSlot } from '@/types/calendar'

// Common timezones for international users
const COMMON_TIMEZONES = [
  // Americas
  { value: 'America/New_York', label: 'Eastern Time (ET)', region: 'Americas' },
  { value: 'America/Chicago', label: 'Central Time (CT)', region: 'Americas' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', region: 'Americas' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', region: 'Americas' },
  { value: 'America/Anchorage', label: 'Alaska Time', region: 'Americas' },
  { value: 'America/Sao_Paulo', label: 'São Paulo', region: 'Americas' },
  { value: 'America/Mexico_City', label: 'Mexico City', region: 'Americas' },
  // Europe
  { value: 'Europe/London', label: 'London (GMT/BST)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Central European', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin', region: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow', region: 'Europe' },
  // Asia
  { value: 'Asia/Dubai', label: 'Dubai (GST)', region: 'Asia' },
  { value: 'Asia/Kolkata', label: 'India (IST)', region: 'Asia' },
  { value: 'Asia/Bangkok', label: 'Bangkok (ICT)', region: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)', region: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)', region: 'Asia' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', region: 'Asia' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)', region: 'Asia' },
  // Pacific / Oceania
  { value: 'Australia/Sydney', label: 'Sydney (AEST)', region: 'Pacific' },
  { value: 'Australia/Melbourne', label: 'Melbourne', region: 'Pacific' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST)', region: 'Pacific' },
]

// Get timezone abbreviation for display
function getTimezoneAbbr(timezone: string, date: Date = new Date()): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    })
    const parts = formatter.formatToParts(date)
    const tzPart = parts.find(p => p.type === 'timeZoneName')
    return tzPart?.value || timezone
  } catch {
    return timezone
  }
}

// Get friendly timezone label
function getTimezoneLabel(timezone: string): string {
  const found = COMMON_TIMEZONES.find(tz => tz.value === timezone)
  if (found) return found.label
  return timezone.replace(/_/g, ' ').replace(/\//g, ' / ')
}

type BookingStep = 'date' | 'time' | 'details' | 'confirmation'

export function PublicBookingPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()

  const { bookingPage, isLoading: pageLoading, error: pageError } = useBookingPage(slug || null)
  const { createBooking, isLoading: bookingLoading, error: bookingError } = useCreateBooking()

  const [step, setStep] = useState<BookingStep>('date')
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null)
  const [bookerName, setBookerName] = useState('')
  const [bookerEmail, setBookerEmail] = useState('')
  const [bookerNotes, setBookerNotes] = useState('')
  const [confirmedBooking, setConfirmedBooking] = useState<{
    start: string
    end: string
    meetingLink?: string
  } | null>(null)
  const [selectedTimezone, setSelectedTimezone] = useState(() =>
    Intl.DateTimeFormat().resolvedOptions().timeZone
  )

  // Get timezone abbreviation
  const timezoneAbbr = getTimezoneAbbr(selectedTimezone)

  // Calculate date range for availability
  const today = new Date()
  const maxDaysAhead = bookingPage?.maxDaysAhead || 30
  const startDate = today.toISOString().split('T')[0]
  const endDate = new Date(today.getTime() + maxDaysAhead * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0]

  const {
    dayAvailability,
    isLoading: availabilityLoading,
  } = useAvailabilityForDateRange(
    bookingPage?.adminUserId,
    startDate,
    endDate,
    bookingPage?.defaultDuration || 30
  )

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(today)

  // Get calendar grid for current month
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDay = firstDay.getDay()

    const days: (Date | null)[] = []

    // Add empty cells for days before first of month
    for (let i = 0; i < startingDay; i++) {
      days.push(null)
    }

    // Add actual days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i))
    }

    return days
  }, [currentMonth])

  // Check if date is available
  const isDateAvailable = (date: Date) => {
    // Use local date format to match dayAvailability data
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    const dateStr = `${y}-${m}-${d}`

    const dayData = dayAvailability.find(day => day.date === dateStr)
    if (!dayData) return false

    return dayData.isAvailable
  }

  // Get available slots for selected date
  const selectedDateSlots = useMemo(() => {
    if (!selectedDate) return []
    const dayData = dayAvailability.find(d => d.date === selectedDate)
    if (!dayData) return []

    // Filter out past slots (already started)
    const now = new Date()

    return dayData.slots.filter(slot => {
      const slotStart = new Date(slot.start)
      return slot.available && slotStart > now
    })
  }, [selectedDate, dayAvailability])

  // Handle booking submission
  const handleSubmit = async () => {
    if (!bookingPage || !selectedSlot) return

    const result = await createBooking({
      bookingPageId: bookingPage.id,
      bookerName,
      bookerEmail,
      bookerNotes,
      scheduledStart: selectedSlot.start,
      scheduledEnd: selectedSlot.end,
    })

    if (result) {
      setConfirmedBooking({
        start: selectedSlot.start,
        end: selectedSlot.end,
        meetingLink: result.meetingRoomLink,
      })
      setStep('confirmation')
    }
  }

  // Format time for display in selected timezone
  const formatTime = (isoString: string) => {
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: selectedTimezone,
    })
  }

  // Format date for display in selected timezone
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: selectedTimezone,
    })
  }

  // Loading state
  if (pageLoading) {
    return (
      <div className="min-h-screen bg-[var(--bg-main)] flex items-center justify-center">
        <div className="flex items-center gap-3 text-[var(--text-muted)]">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading booking page...</span>
        </div>
      </div>
    )
  }

  // Error state
  if (pageError || !bookingPage) {
    return (
      <div className="min-h-screen bg-[var(--bg-main)] flex items-center justify-center">
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-8 max-w-md w-full mx-4 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-[var(--text-main)] mb-2">
            Booking Page Not Found
          </h1>
          <p className="text-[var(--text-muted)]">
            {pageError || 'This booking page does not exist or is no longer active.'}
          </p>
        </div>
      </div>
    )
  }

  // Host name
  const hostName = bookingPage.user
    ? `${bookingPage.user.firstName || ''} ${bookingPage.user.lastName || ''}`.trim() ||
      bookingPage.user.email
    : 'Unknown'

  return (
    <div className="min-h-screen bg-[var(--bg-main)] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6 mb-6">
          <div className="flex items-center gap-4">
            {bookingPage.avatarUrl ? (
              <img
                src={bookingPage.avatarUrl}
                alt={hostName}
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[var(--primary)]/10 flex items-center justify-center">
                <User className="w-8 h-8 text-[var(--primary)]" />
              </div>
            )}
            <div>
              <p className="text-sm text-[var(--text-muted)] mb-1">{hostName}</p>
              <h1 className="text-2xl font-bold text-[var(--text-main)]">
                {bookingPage.title}
              </h1>
              {bookingPage.description && (
                <p className="text-[var(--text-muted)] mt-1">{bookingPage.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 mt-4 text-sm text-[var(--text-muted)]">
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              <span>{bookingPage.defaultDuration} minutes</span>
            </div>
            {(bookingPage.allowVideoConference || bookingPage.requireVideoConference) && (
              <div className="flex items-center gap-1.5">
                <Video className="w-4 h-4" />
                <span>Video call included</span>
              </div>
            )}
          </div>
        </div>

        {/* Progress Indicator */}
        {step !== 'confirmation' && (
          <div className="flex items-center gap-2 mb-6">
            {(['date', 'time', 'details'] as const).map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === s
                      ? 'bg-[var(--primary)] text-white'
                      : ['date', 'time', 'details'].indexOf(step) > i
                      ? 'bg-green-500 text-white'
                      : 'bg-[var(--bg-card)] text-[var(--text-muted)] border border-[var(--border)]'
                  }`}
                >
                  {['date', 'time', 'details'].indexOf(step) > i ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    i + 1
                  )}
                </div>
                {i < 2 && (
                  <div
                    className={`w-12 h-0.5 ${
                      ['date', 'time', 'details'].indexOf(step) > i
                        ? 'bg-green-500'
                        : 'bg-[var(--border)]'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Step: Date Selection */}
        {step === 'date' && (
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[var(--text-main)] mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Select a Date
            </h2>

            {/* Timezone Selector */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Globe className="w-4 h-4 text-[var(--primary)]" />
                <span className="text-sm font-medium text-[var(--text-main)]">
                  Your Timezone
                </span>
              </div>
              <select
                value={selectedTimezone}
                onChange={(e) => setSelectedTimezone(e.target.value)}
                className="w-full px-4 py-2.5 bg-[var(--bg-main)] border border-[var(--border)] rounded-lg text-[var(--text-main)] text-sm focus:outline-none focus:border-[var(--primary)]"
              >
                {/* Current timezone if not in list */}
                {!COMMON_TIMEZONES.find(tz => tz.value === selectedTimezone) && (
                  <option value={selectedTimezone}>
                    {getTimezoneLabel(selectedTimezone)} ({timezoneAbbr})
                  </option>
                )}
                <optgroup label="Americas">
                  {COMMON_TIMEZONES.filter(tz => tz.region === 'Americas').map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({getTimezoneAbbr(tz.value)})
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Europe">
                  {COMMON_TIMEZONES.filter(tz => tz.region === 'Europe').map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({getTimezoneAbbr(tz.value)})
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Asia">
                  {COMMON_TIMEZONES.filter(tz => tz.region === 'Asia').map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({getTimezoneAbbr(tz.value)})
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Pacific / Oceania">
                  {COMMON_TIMEZONES.filter(tz => tz.region === 'Pacific').map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({getTimezoneAbbr(tz.value)})
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>

            {/* Calendar Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() =>
                  setCurrentMonth(
                    new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
                  )
                }
                className="p-2 hover:bg-[var(--bg-main)] rounded-lg transition-colors"
                disabled={
                  currentMonth.getMonth() === today.getMonth() &&
                  currentMonth.getFullYear() === today.getFullYear()
                }
              >
                <ChevronLeft className="w-5 h-5 text-[var(--text-muted)]" />
              </button>
              <span className="text-[var(--text-main)] font-medium">
                {currentMonth.toLocaleDateString('en-US', {
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
              <button
                onClick={() =>
                  setCurrentMonth(
                    new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
                  )
                }
                className="p-2 hover:bg-[var(--bg-main)] rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-[var(--text-muted)]" />
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div
                  key={day}
                  className="text-center text-sm text-[var(--text-muted)] py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, i) => {
                if (!day) {
                  return <div key={`empty-${i}`} className="p-2" />
                }

                // Use local date format consistently
                const y = day.getFullYear()
                const m = String(day.getMonth() + 1).padStart(2, '0')
                const d = String(day.getDate()).padStart(2, '0')
                const dateStr = `${y}-${m}-${d}`

                const todayY = today.getFullYear()
                const todayM = String(today.getMonth() + 1).padStart(2, '0')
                const todayD = String(today.getDate()).padStart(2, '0')
                const todayStr = `${todayY}-${todayM}-${todayD}`

                const isToday = dateStr === todayStr
                const isSelected = dateStr === selectedDate
                const isPast = day < new Date(today.getFullYear(), today.getMonth(), today.getDate())
                const isAvailable = !isPast && isDateAvailable(day)

                return (
                  <button
                    key={dateStr}
                    onClick={() => {
                      if (isAvailable) {
                        setSelectedDate(dateStr)
                        setSelectedSlot(null)
                        setStep('time')
                      }
                    }}
                    disabled={!isAvailable}
                    className={`
                      p-2 rounded-lg text-sm transition-colors relative
                      ${isSelected ? 'bg-[var(--primary)] text-white' : ''}
                      ${!isSelected && isAvailable ? 'hover:bg-[var(--primary)]/10 text-[var(--text-main)]' : ''}
                      ${!isAvailable ? 'text-[var(--text-muted)] opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    {day.getDate()}
                    {isToday && (
                      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[var(--primary)]" />
                    )}
                  </button>
                )
              })}
            </div>

            {availabilityLoading && (
              <div className="flex items-center justify-center py-4 text-[var(--text-muted)]">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Loading availability...
              </div>
            )}
          </div>
        )}

        {/* Step: Time Selection */}
        {step === 'time' && selectedDate && (
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
            <button
              onClick={() => setStep('date')}
              className="flex items-center gap-1 text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] mb-4"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to calendar
            </button>

            <h2 className="text-lg font-semibold text-[var(--text-main)] mb-2 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Select a Time
            </h2>
            <p className="text-[var(--text-muted)] mb-1">{formatDate(selectedDate)}</p>
            <p className="text-sm text-[var(--text-muted)] mb-4 flex items-center gap-1.5 opacity-75">
              <Globe className="w-3.5 h-3.5" />
              Times shown in {getTimezoneLabel(selectedTimezone)} ({timezoneAbbr})
            </p>

            {selectedDateSlots.length === 0 ? (
              <p className="text-[var(--text-muted)] text-center py-8">
                No available times on this date. Please select another date.
              </p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {selectedDateSlots.map(slot => (
                  <button
                    key={slot.start}
                    onClick={() => {
                      setSelectedSlot(slot)
                      setStep('details')
                    }}
                    className={`
                      px-4 py-2 rounded-lg text-sm font-medium transition-colors border
                      ${
                        selectedSlot?.start === slot.start
                          ? 'bg-[var(--primary)] text-white border-[var(--primary)]'
                          : 'bg-[var(--bg-card)] text-[var(--text-main)] border-[var(--border)] hover:border-[var(--primary)]'
                      }
                    `}
                  >
                    {formatTime(slot.start)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step: Details */}
        {step === 'details' && selectedSlot && (
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
            <button
              onClick={() => setStep('time')}
              className="flex items-center gap-1 text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] mb-4"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to time selection
            </button>

            <h2 className="text-lg font-semibold text-[var(--text-main)] mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Your Details
            </h2>

            {/* Selected Time Summary */}
            <div className="bg-[var(--bg-main)] rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-[var(--primary)]" />
                <div>
                  <p className="text-sm font-medium text-[var(--text-main)]">
                    {formatDate(selectedDate!)}
                  </p>
                  <p className="text-sm text-[var(--text-muted)]">
                    {formatTime(selectedSlot.start)} - {formatTime(selectedSlot.end)} ({timezoneAbbr})
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-main)] mb-1.5">
                  <span className="flex items-center gap-1.5">
                    <User className="w-4 h-4" />
                    Your Name *
                  </span>
                </label>
                <input
                  type="text"
                  value={bookerName}
                  onChange={e => setBookerName(e.target.value)}
                  placeholder="John Doe"
                  className="w-full px-4 py-2.5 bg-[var(--bg-main)] border border-[var(--border)] rounded-lg text-[var(--text-main)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--primary)]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-main)] mb-1.5">
                  <span className="flex items-center gap-1.5">
                    <Mail className="w-4 h-4" />
                    Your Email *
                  </span>
                </label>
                <input
                  type="email"
                  value={bookerEmail}
                  onChange={e => setBookerEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="w-full px-4 py-2.5 bg-[var(--bg-main)] border border-[var(--border)] rounded-lg text-[var(--text-main)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--primary)]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-main)] mb-1.5">
                  <span className="flex items-center gap-1.5">
                    <FileText className="w-4 h-4" />
                    Notes (optional)
                  </span>
                </label>
                <textarea
                  value={bookerNotes}
                  onChange={e => setBookerNotes(e.target.value)}
                  placeholder="What would you like to discuss?"
                  rows={3}
                  className="w-full px-4 py-2.5 bg-[var(--bg-main)] border border-[var(--border)] rounded-lg text-[var(--text-main)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--primary)] resize-none"
                />
              </div>

              {bookingError && (
                <div className="flex items-center gap-2 text-red-500 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {bookingError}
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={!bookerName || !bookerEmail || bookingLoading}
                className="w-full py-3 bg-[var(--primary)] text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {bookingLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Scheduling...
                  </>
                ) : (
                  'Confirm Booking'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step: Confirmation */}
        {step === 'confirmation' && confirmedBooking && (
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>

            <h2 className="text-2xl font-bold text-[var(--text-main)] mb-2">
              Booking Confirmed!
            </h2>
            <p className="text-[var(--text-muted)] mb-6">
              You're scheduled with {hostName}
            </p>

            <div className="bg-[var(--bg-main)] rounded-lg p-4 mb-6 text-left">
              <div className="flex items-center gap-3 mb-3">
                <Calendar className="w-5 h-5 text-[var(--primary)]" />
                <div>
                  <p className="text-sm font-medium text-[var(--text-main)]">
                    {formatDate(selectedDate!)}
                  </p>
                  <p className="text-sm text-[var(--text-muted)]">
                    {formatTime(confirmedBooking.start)} - {formatTime(confirmedBooking.end)} ({timezoneAbbr})
                  </p>
                </div>
              </div>

              {confirmedBooking.meetingLink && (
                <div className="flex items-center gap-3">
                  <Video className="w-5 h-5 text-[var(--primary)]" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--text-main)] mb-1">
                      Meeting Link
                    </p>
                    <a
                      href={confirmedBooking.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[var(--primary)] hover:underline flex items-center gap-1 truncate"
                    >
                      {confirmedBooking.meetingLink}
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                  </div>
                </div>
              )}
            </div>

            <p className="text-sm text-[var(--text-muted)]">
              A confirmation email has been sent to <strong>{bookerEmail}</strong>
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default PublicBookingPage
